"use client"

import { useMemo } from "react"
import Box from "@mui/material/Box"
import Dialog from "@mui/material/Dialog"
import DialogContent from "@mui/material/DialogContent"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import LinearProgress from "@mui/material/LinearProgress"
import CircularProgress from "@mui/material/CircularProgress"
import Chip from "@mui/material/Chip"
import Divider from "@mui/material/Divider"
import Avatar from "@mui/material/Avatar"
import Button from "@mui/material/Button"
import IconButton from "@mui/material/IconButton"
import { alpha } from "@mui/material/styles"
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded"
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded"
import StorageRoundedIcon from "@mui/icons-material/StorageRounded"
import CloseRoundedIcon from "@mui/icons-material/CloseRounded"
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded"
import FileDownloadIcon from "@mui/icons-material/FileDownload"
import { Alert, AlertTitle } from "@mui/material"
import { useExcelDownloadLoaderStore, downloadFileFromUrl } from "./useExcelDownloadLoader.store"
import { useAuthStore } from "@/store/auth"

type Step = {
  label: string
  threshold: number
}

const STEPS: Step[] = [
  { label: "Iniciando generación del reporte", threshold: 20 },
  { label: "Consultando registros", threshold: 40 },
  { label: "Calculando históricos", threshold: 60 },
  { label: "Generando hoja de cálculo", threshold: 80 },
  { label: "Enviando al correo", threshold: 100 },
]

export default function ExcelDownloadLoader() {

  const open = useExcelDownloadLoaderStore(s => s.open)
  const status = useExcelDownloadLoaderStore(s => s.status)
  const progress = useExcelDownloadLoaderStore(s => s.progress)
  const infoStatus = useExcelDownloadLoaderStore(s => s.infoStatus)
  const totalRegistros = useExcelDownloadLoaderStore(s => s.totalRegistros)
  const fileUrl = useExcelDownloadLoaderStore(s => s.fileUrl)
  const fileName = useExcelDownloadLoaderStore(s => s.fileName)
  const close = useExcelDownloadLoaderStore(s => s.close)
  const email = useAuthStore(s => s.user)?.email

  const activeStep = useMemo(() => {
    if (status === 'IDLE' || status === 'STARTING') return 0
    const idx = STEPS.findIndex(s => progress.percent < s.threshold)
    return idx === -1 ? STEPS.length - 1 : idx
  }, [progress.percent, status])

  const isComplete = status === 'SUCCESS'
  const isError = status === 'FAILURE' || status === 'ERROR'
  const isProcessing = status === 'PROGRESS' || status === 'STARTING'

  return (
    <Dialog
      open={open}
      onClose={isComplete || isError ? close : undefined}
      maxWidth="xs"
      fullWidth
      slotProps={{
        backdrop: {
          sx: { backgroundColor: (t) => alpha(t.palette.text.primary, 0.4) },
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Stack direction="row" spacing={1.5} sx={{ mb: 0.5, alignItems: "center" }}>
          <Avatar
            variant="rounded"
            sx={{ bgcolor: isError ? "error.main" : "primary.main", width: 36, height: 36, fontWeight: 700, fontSize: 14 }}
          >
            EVA
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
              Módulo de Reportes
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Generación de Excel
            </Typography>
          </Box>
          <Chip
            size="small"
            label={isComplete ? "Listo" : isError ? "Error" : "Exportando"}
            color={isComplete ? "success" : isError ? "error" : "primary"}
            variant={isComplete || isError ? "filled" : "outlined"}
          />
          {(isComplete || isError) && (
            <IconButton size="small" onClick={close} aria-label="Cerrar" sx={{ ml: 0.5 }}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>

        <Divider sx={{ my: 2.5 }} />

        {isError ? (
          <Alert severity="error" sx={{ mb: 2.5 }}>
            <AlertTitle>Error en la generación</AlertTitle>
            {infoStatus || "Ocurrió un error inesperado. Intente nuevamente."}
          </Alert>
        ) : (
          <>
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" sx={{ mb: 0.75, justifyContent: "space-between" }}>
                <Typography variant="caption" color="text.secondary">
                  {infoStatus || STEPS[activeStep].label}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "primary.main" }}>
                  {Math.round(progress.percent)}%
                </Typography>
              </Stack>
              <LinearProgress
                //variant={isProcessing ? "determinate" : "indeterminate"}
                variant="determinate"
                value={isProcessing ? progress.percent : undefined}
                sx={{ height: 8, borderRadius: 99, bgcolor: (t) => alpha(t.palette.primary.main, 0.12) }}
              />
              <Stack direction="row" spacing={0.75} sx={{ mt: 1, alignItems: "center" }}>
                <StorageRoundedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary">
                  {totalRegistros > 0
                    ? `${totalRegistros.toLocaleString("es-ES")} registros encontrados`
                    : isProcessing
                      ? "Recuperando registros..."
                      : "—"}
                </Typography>
              </Stack>
            </Box>

            <Stack
              spacing={0.5}
              sx={{
                bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                borderRadius: 2,
                p: 1.5,
              }}
            >
              {STEPS.map((step, index) => {
                const done = index < activeStep || isComplete
                const current = index === activeStep && isProcessing
                const failed = index === activeStep && isError
                return (
                  <Stack key={step.label} direction="row" spacing={1.25} sx={{ py: 0.5, alignItems: "center" }}>
                    {done ? (
                      <CheckCircleRoundedIcon sx={{ fontSize: 20, color: "secondary.main" }} />
                    ) : current ? (
                      <CircularProgress size={16} thickness={5} sx={{ color: "primary.main", mx: "2px" }} />
                    ) : failed ? (
                      <ErrorOutlineRoundedIcon sx={{ fontSize: 20, color: "error.main" }} />
                    ) : (
                      <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 20, color: "text.disabled" }} />
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        flexGrow: 1,
                        color: done || current ? "text.primary" : "text.secondary",
                        fontWeight: current ? 600 : 400,
                      }}
                    >
                      {step.label}
                    </Typography>
                  </Stack>
                )
              })}
            </Stack>
          </>
        )}

        {isComplete ? (
          <Alert severity="success" sx={{ mt: 2.5 }}>
            <AlertTitle>El archivo se generó correctamente!</AlertTitle>
            {fileUrl ? (
              <Stack spacing={1} sx={{ mt: 0.5 }}>
                <Typography variant="body2">
                  La descarga inició automáticamente. Si no comenzó, puede descargarla aquí:
                </Typography>
                <Button
                  variant="outlined"
                  color="success"
                  size="small"
                  startIcon={<FileDownloadIcon />}
                  onClick={() => downloadFileFromUrl(fileUrl, fileName ?? undefined)}
                  sx={{ alignSelf: "flex-start", mt: 0.5 }}
                >
                  Descargar Reporte
                </Button>
              </Stack>
            ) : (
              `Se ha enviado al correo empresarial proporcionado: ${email}`
            )}
          </Alert>
        ) : isError ? (
          <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
            <Button variant="contained" color="error" onClick={close}>
              Cerrar
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" spacing={0.75} sx={{ mt: 2.5, alignItems: "center", justifyContent: "start" }}>
            <Button variant="text" onClick={close}>
              Cancelar
            </Button>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  )
}
